import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  FileText, 
  Calendar, 
  CreditCard, 
  Building, 
  Users, 
  BookOpen, 
  Library,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  ChevronRight,
  Home,
  Shield,
  Globe,
  Heart
} from 'lucide-react';
import './Services.css';

const Services: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('academic');

  const serviceCategories = [
    {
      id: 'academic',
      title: 'Acadêmicos',
      description: 'Serviços para estudantes e professores',
      icon: <GraduationCap size={24} />,
      color: '#6b7280',
      gradient: 'linear-gradient(135deg, #6b7280, #4b5563)'
    },
    {
      id: 'administrative',
      title: 'Administrativos',
      description: 'Gestão e protocolo institucional',
      icon: <Building size={24} />,
      color: '#6b7280',
      gradient: 'linear-gradient(135deg, #6b7280, #4b5563)'
    },
    {
      id: 'technology',
      title: 'Tecnologia',
      description: 'Sistemas e recursos digitais',
      icon: <Globe size={24} />,
      color: '#6b7280',
      gradient: 'linear-gradient(135deg, #6b7280, #4b5563)'
    },
    {
      id: 'student',
      title: 'Vida Estudantil',
      description: 'Atividades e bem-estar estudantil',
      icon: <Heart size={24} />,
      color: '#6b7280',
      gradient: 'linear-gradient(135deg, #6b7280, #4b5563)'
    }
  ];

  const allServices = {
    academic: [
      {
        icon: <GraduationCap size={32} />,
        title: "Matrícula",
        description: "Processo de matrícula e rematrícula para novos e veteranos estudantes",
        color: "#6b7280"
      },
      {
        icon: <FileText size={32} />,
        title: "Histórico Escolar",
        description: "Solicitação e emissão de histórico escolar e certificados",
        color: "#6b7280"
      },
      {
        icon: <Calendar size={32} />,
        title: "Calendário Acadêmico",
        description: "Consultar datas importantes do calendário letivo",
        color: "#6b7280"
      },
      {
        icon: <BookOpen size={32} />,
        title: "Biblioteca Digital",
        description: "Acesso ao acervo digital e recursos de pesquisa",
        color: "#6b7280"
      },
      {
        icon: <Library size={32} />,
        title: "SIGAA",
        description: "Sistema Integrado de Gestão de Atividades Acadêmicas",
        color: "#6b7280"
      },
      {
        icon: <Users size={32} />,
        title: "Coordenações",
        description: "Contato com coordenações de cursos e departamentos",
        color: "#6b7280"
      }
    ],
    administrative: [
      {
        icon: <Building size={32} />,
        title: "Protocolo",
        description: "Protocolo de documentos e processos administrativos",
        color: "#6b7280"
      },
      {
        icon: <CreditCard size={32} />,
        title: "Financeiro",
        description: "Pagamentos, boletos e informações financeiras",
        color: "#6b7280"
      },
      {
        icon: <FileText size={32} />,
        title: "Documentação",
        description: "Emissão de documentos oficiais e declarações",
        color: "#6b7280"
      },
      {
        icon: <Mail size={32} />,
        title: "Comunicação",
        description: "Canais de comunicação e informações institucionais",
        color: "#6b7280"
      },
      {
        icon: <Phone size={32} />,
        title: "Suporte",
        description: "Central de atendimento e suporte técnico",
        color: "#6b7280"
      },
      {
        icon: <MapPin size={32} />,
        title: "Localização",
        description: "Informações sobre campus e localização",
        color: "#6b7280"
      }
    ],
    technology: [
      {
        icon: <Globe size={32} />,
        title: "Wi-Fi Campus",
        description: "Acesso à rede wireless em todos os campi",
        color: "#6b7280"
      },
      {
        icon: <Shield size={32} />,
        title: "VPN UNIFAP",
        description: "Acesso remoto seguro aos sistemas institucionais",
        color: "#6b7280"
      },
      {
        icon: <Library size={32} />,
        title: "Sistemas Integrados",
        description: "Portal único para todos os sistemas da UNIFAP",
        color: "#6b7280"
      },
      {
        icon: <Mail size={32} />,
        title: "Email Institucional",
        description: "Acesso ao email corporativo da UNIFAP",
        color: "#6b7280"
      },
      {
        icon: <FileText size={32} />,
        title: "Cloud Storage",
        description: "Armazenamento em nuvem para documentos",
        color: "#6b7280"
      },
      {
        icon: <Users size={32} />,
        title: "Suporte TI",
        description: "Suporte técnico especializado",
        color: "#6b7280"
      }
    ],
    student: [
      {
        icon: <Heart size={32} />,
        title: "Assistência Estudantil",
        description: "Programas de apoio e bolsas estudantis",
        color: "#6b7280"
      },
      {
        icon: <Users size={32} />,
        title: "Centro Acadêmico",
        description: "Representação estudantil e atividades",
        color: "#6b7280"
      },
      {
        icon: <Home size={32} />,
        title: "Moradia Estudantil",
        description: "Informações sobre alojamento universitário",
        color: "#6b7280"
      },
      {
        icon: <Shield size={32} />,
        title: "Saúde Estudantil",
        description: "Atendimento médico e psicológico",
        color: "#6b7280"
      },
      {
        icon: <Calendar size={32} />,
        title: "Eventos Culturais",
        description: "Atividades culturais e de lazer",
        color: "#6b7280"
      },
      {
        icon: <Globe size={32} />,
        title: "Intercâmbio",
        description: "Programas de mobilidade acadêmica",
        color: "#6b7280"
      }
    ]
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  const activeCategoryData = serviceCategories.find(cat => cat.id === activeCategory);
  const currentServices = allServices[activeCategory as keyof typeof allServices] || [];

  return (
    <section className="services" id="servicos">
      <div className="services-container">
        <motion.div 
          className="services-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Serviços UNIFAP</h2>
          <p>Acesse todos os serviços da Universidade Federal do Amapá de forma rápida e organizada</p>
        </motion.div>

        {/* Navegação por Categorias */}
        <motion.div 
          className="categories-navigation"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="categories-grid">
            {serviceCategories.map((category) => (
              <motion.button
                key={category.id}
                className={`category-card ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: activeCategory === category.id ? category.gradient : 'rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="category-icon">
                  {category.icon}
                </div>
                <div className="category-content">
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                </div>
                <ChevronRight 
                  size={20} 
                  className={`category-arrow ${activeCategory === category.id ? 'active' : ''}`}
                />
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Seção de Serviços Ativa */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            className="active-services-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5 }}
          >
            {activeCategoryData && (
              <div className="section-header">
                <div 
                  className="section-icon"
                  style={{ background: activeCategoryData.gradient }}
                >
                  {activeCategoryData.icon}
                </div>
                <h3>{activeCategoryData.title}</h3>
                <p>{activeCategoryData.description}</p>
              </div>
            )}

            <motion.div 
              className="services-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {currentServices.map((service, index) => (
                <motion.div 
                  key={index}
                  className="service-card glass"
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.05,
                    y: -5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div 
                    className="service-icon"
                    style={{ backgroundColor: service.color }}
                  >
                    {service.icon}
                  </div>
                  <h4>{service.title}</h4>
                  <p>{service.description}</p>
                  <button className="service-btn">
                    <span>Acessar</span>
                    <ExternalLink size={16} />
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Seção de Destaque */}
        <motion.div 
          className="highlight-section glass-dark"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="highlight-content">
            <h3>Precisa de Ajuda?</h3>
            <p>Nossa equipe está pronta para auxiliar você em qualquer dúvida sobre os serviços da UNIFAP</p>
            <div className="highlight-actions">
              <button className="highlight-btn primary">
                <Phone size={20} />
                <span>Fale Conosco</span>
              </button>
              <button className="highlight-btn secondary">
                <Mail size={20} />
                <span>Enviar Email</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services; 